import * as React from 'react'
import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  Clock,
  ExternalLink,
  FileText,
  Hash,
  Lock,
  Package,
  Percent,
  Receipt,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import { InvoiceStatusEnum, QuotePaymentScheduleAmountTypeEnum, QuoteStatusEnum } from '@/gen/cortado/v1/settings_pb'
import type { InvoiceLineItem, PaymentSchedule, QuoteLineItem, QuotePaymentSchedule } from '@/gen/cortado/v1/invoices_pb'
import { formatCurrency } from '@/lib/currency-utils'
import { formatTimestamp, timestampToDateOnly } from '@/lib/date-utils'
import { formatQuotePaymentScheduleDueFromProto } from '@/lib/quote-payment-schedule-defaults'
import { cn } from '@/lib/utils'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { SafeHtml } from '@/components/shared/safe-html'

type DocumentKind = 'quote' | 'invoice'
type BadgeVariant = React.ComponentProps<typeof Badge>['variant']

interface StatusConfig {
  label: string
  variant: BadgeVariant
  icon: LucideIcon
  bannerClassName: string
  message: string
}

const quoteStatusConfig: Record<number, StatusConfig> = {
  [QuoteStatusEnum.QUOTE_STATUS_DRAFT]: {
    label: 'Draft',
    variant: 'secondary',
    icon: FileText,
    bannerClassName: 'border-status-neutral-border bg-status-neutral-bg text-status-neutral-fg',
    message: 'This quote is internal and hidden from the client portal.',
  },
  [QuoteStatusEnum.QUOTE_STATUS_SENT]: {
    label: 'Live in portal',
    variant: 'info',
    icon: ExternalLink,
    bannerClassName: 'border-status-info-border bg-status-info-bg text-status-info-fg',
    message: 'This quote is visible to the client in the portal.',
  },
  [QuoteStatusEnum.QUOTE_STATUS_VIEWED]: {
    label: 'Viewed',
    variant: 'purple',
    icon: ExternalLink,
    bannerClassName: 'border-purple-200 bg-purple-50 text-purple-800 dark:border-purple-800 dark:bg-purple-950/30 dark:text-purple-200',
    message: 'This quote is live in the client portal and has been viewed.',
  },
  [QuoteStatusEnum.QUOTE_STATUS_REVISED]: {
    label: 'Revised',
    variant: 'warning',
    icon: Clock,
    bannerClassName: 'border-status-warning-border bg-status-warning-bg text-status-warning-fg',
    message: 'This quote has a newer revision.',
  },
  [QuoteStatusEnum.QUOTE_STATUS_ACCEPTED]: {
    label: 'Accepted',
    variant: 'success',
    icon: CheckCircle2,
    bannerClassName: 'border-status-success-border bg-status-success-bg text-status-success-fg',
    message: 'This quote has been accepted.',
  },
  [QuoteStatusEnum.QUOTE_STATUS_REJECTED]: {
    label: 'Cancelled',
    variant: 'danger',
    icon: AlertTriangle,
    bannerClassName: 'border-status-danger-border bg-status-danger-bg text-status-danger-fg',
    message: 'This quote was rejected by the customer.',
  },
  [QuoteStatusEnum.QUOTE_STATUS_EXPIRED]: {
    label: 'Expired',
    variant: 'danger',
    icon: AlertTriangle,
    bannerClassName: 'border-status-danger-border bg-status-danger-bg text-status-danger-fg',
    message: 'This quote is past its valid-until date.',
  },
  [QuoteStatusEnum.QUOTE_STATUS_CANCELLED]: {
    label: 'Cancelled',
    variant: 'secondary',
    icon: AlertTriangle,
    bannerClassName: 'border-muted bg-muted/50 text-muted-foreground',
    message: 'This quote is no longer active.',
  },
}

const invoiceStatusConfig: Record<number, StatusConfig> = {
  [InvoiceStatusEnum.INVOICE_STATUS_DRAFT]: {
    label: 'Draft',
    variant: 'secondary',
    icon: FileText,
    bannerClassName: 'border-status-neutral-border bg-status-neutral-bg text-status-neutral-fg',
    message: 'This invoice is internal and hidden from the client portal.',
  },
  [InvoiceStatusEnum.INVOICE_STATUS_SENT]: {
    label: 'Live in portal',
    variant: 'info',
    icon: ExternalLink,
    bannerClassName: 'border-status-info-border bg-status-info-bg text-status-info-fg',
    message: 'This invoice is visible to the client in the portal.',
  },
  [InvoiceStatusEnum.INVOICE_STATUS_VIEWED]: {
    label: 'Viewed',
    variant: 'purple',
    icon: ExternalLink,
    bannerClassName: 'border-purple-200 bg-purple-50 text-purple-800 dark:border-purple-800 dark:bg-purple-950/30 dark:text-purple-200',
    message: 'This invoice is live in the client portal and has been viewed.',
  },
  [InvoiceStatusEnum.INVOICE_STATUS_PAYMENT_PENDING]: {
    label: 'Payment Pending',
    variant: 'warning',
    icon: Clock,
    bannerClassName: 'border-status-warning-border bg-status-warning-bg text-status-warning-fg',
    message: 'A customer payment is currently pending.',
  },
  [InvoiceStatusEnum.INVOICE_STATUS_PARTIALLY_PAID]: {
    label: 'Partially Paid',
    variant: 'warning',
    icon: Clock,
    bannerClassName: 'border-status-warning-border bg-status-warning-bg text-status-warning-fg',
    message: 'This invoice has a remaining balance.',
  },
  [InvoiceStatusEnum.INVOICE_STATUS_PAID]: {
    label: 'Paid',
    variant: 'success',
    icon: CheckCircle2,
    bannerClassName: 'border-status-success-border bg-status-success-bg text-status-success-fg',
    message: 'This invoice is fully paid.',
  },
  [InvoiceStatusEnum.INVOICE_STATUS_OVERDUE]: {
    label: 'Overdue',
    variant: 'danger',
    icon: AlertTriangle,
    bannerClassName: 'border-status-danger-border bg-status-danger-bg text-status-danger-fg',
    message: 'This invoice has an overdue balance.',
  },
  [InvoiceStatusEnum.INVOICE_STATUS_CANCELLED]: {
    label: 'Cancelled',
    variant: 'secondary',
    icon: AlertTriangle,
    bannerClassName: 'border-muted bg-muted/50 text-muted-foreground',
    message: 'This invoice is no longer active.',
  },
  [InvoiceStatusEnum.INVOICE_STATUS_OVERPAID]: {
    label: 'Overpaid',
    variant: 'success',
    icon: CheckCircle2,
    bannerClassName: 'border-status-success-border bg-status-success-bg text-status-success-fg',
    message: 'This invoice has received more than the amount due.',
  },
}

function getDocumentStatusConfig(kind: DocumentKind, status: number): StatusConfig {
  const config = kind === 'quote' ? quoteStatusConfig[status] : invoiceStatusConfig[status]
  return config ?? {
    label: 'Unknown',
    variant: 'secondary',
    icon: FileText,
    bannerClassName: 'border-muted bg-muted/50 text-muted-foreground',
    message: 'This document status is unknown.',
  }
}

export function getDocumentStatusLabel(kind: DocumentKind, status: number): string {
  return getDocumentStatusConfig(kind, status).label
}

export function DocumentStatusBadge({
  kind,
  status,
  className,
}: {
  kind: DocumentKind
  status: number
  className?: string
}) {
  const config = getDocumentStatusConfig(kind, status)
  return (
    <Badge variant={config.variant} className={className}>
      {config.label}
    </Badge>
  )
}

export function DocumentStatusBanner({
  kind,
  status,
  title,
  detail,
}: {
  kind: DocumentKind
  status: number
  title: string
  detail?: string
}) {
  const config = getDocumentStatusConfig(kind, status)
  const StatusIcon = config.icon

  return (
    <div className={cn('rounded-lg border p-4', config.bannerClassName)}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <StatusIcon className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <div className="min-w-0">
            <p className="font-medium">{title}</p>
            <p className="mt-1 text-sm opacity-85">{detail || config.message}</p>
          </div>
        </div>
        <DocumentStatusBadge kind={kind} status={status} />
      </div>
    </div>
  )
}

interface DocumentSummaryCardProps {
  kind: DocumentKind
  status: number
  numberLabel: string
  title?: string
  totalCents: number
  subtotalCents?: number
  discountCents?: number
  taxCents?: number
  tipCents?: number
  paidCents?: number
  dueCents?: number
  createdAt?: unknown
  sentAt?: unknown
  viewedAt?: unknown
  acceptedAt?: unknown
  validUntil?: unknown
  isLocked?: boolean
}

export function DocumentSummaryCard({
  kind,
  status,
  numberLabel,
  title,
  totalCents,
  subtotalCents,
  discountCents,
  taxCents,
  tipCents,
  paidCents,
  dueCents,
  createdAt,
  sentAt,
  viewedAt,
  acceptedAt,
  validUntil,
  isLocked,
}: DocumentSummaryCardProps) {
  const config = getDocumentStatusConfig(kind, status)
  const StatusIcon = config.icon
  const hasPaymentProgress = paidCents !== undefined || dueCents !== undefined
  const totals = getTipAwareTotals({
    subtotalCents,
    discountCents,
    taxCents,
    tipCents,
    totalCents,
  })

  return (
    <Card className="overflow-hidden pt-0">
      <CardHeader className={cn('border-b py-4', config.bannerClassName)}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <StatusIcon className="h-5 w-5 flex-shrink-0" />
            <CardTitle className="truncate text-base">{config.label}</CardTitle>
          </div>
          {isLocked && (
            <div className="flex items-center gap-1 text-xs opacity-80">
              <Lock className="h-3 w-3" />
              <span>Locked</span>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4 p-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Hash className="h-4 w-4" />
          <span className="font-mono">{numberLabel}</span>
        </div>

        {title && <p className="text-sm font-medium">{title}</p>}

        <div>
          <p className="mb-1 text-xs uppercase text-muted-foreground">Total</p>
          <p className="font-financial text-3xl font-bold">${formatCurrency(totals.displayTotalCents)}</p>
        </div>

        <DocumentTotalsRows
          subtotalCents={subtotalCents}
          discountCents={discountCents}
          taxCents={taxCents}
          tipCents={tipCents}
          totalCents={totalCents}
          tipIncludedInTotal={totals.tipIncludedInTotal}
        />

        {hasPaymentProgress && (
          <div className="grid grid-cols-2 gap-3 border-t pt-3">
            <AmountTile label="Paid" amountCents={paidCents ?? 0} className="text-status-success-fg" />
            <AmountTile label="Due" amountCents={dueCents ?? 0} className={(dueCents ?? 0) > 0 ? 'text-status-warning-fg' : undefined} />
          </div>
        )}

        <div className="space-y-2 border-t pt-3 text-sm">
          <DateRow icon={Calendar} label="Created" value={createdAt} />
          <DateRow
            icon={ExternalLink}
            label="Published"
            value={sentAt}
          />
          <DateRow icon={ExternalLink} label="Viewed" value={viewedAt} />
          <DateRow icon={CheckCircle2} label="Accepted" value={acceptedAt} />
          <DateRow icon={Clock} label="Valid Until" value={validUntil} />
        </div>
      </CardContent>
    </Card>
  )
}

function AmountTile({ label, amountCents, className }: { label: string; amountCents: number; className?: string }) {
  return (
    <div className="rounded-lg border bg-muted/30 p-3 text-center">
      <p className={cn('font-financial text-lg font-semibold', className)}>${formatCurrency(amountCents)}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  )
}

function DateRow({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value?: unknown }) {
  if (!value) return null

  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-muted-foreground" />
      <span className="text-muted-foreground">{label}</span>
      <span className="font-financial ml-auto">{formatTimestamp(value)}</span>
    </div>
  )
}

function DocumentTotalsRows({
  subtotalCents,
  discountCents,
  taxCents,
  tipCents,
  totalCents,
  tipIncludedInTotal,
}: {
  subtotalCents?: number
  discountCents?: number
  taxCents?: number
  tipCents?: number
  totalCents: number
  tipIncludedInTotal?: boolean
}) {
  const hasSubtotal = subtotalCents !== undefined
  const hasDiscount = (discountCents ?? 0) > 0
  const hasTax = (taxCents ?? 0) > 0
  const hasTip = (tipCents ?? 0) > 0
  const displayTotalCents = hasTip && tipIncludedInTotal === false ? totalCents + (tipCents ?? 0) : totalCents

  if (!hasSubtotal && !hasDiscount && !hasTax && !hasTip) return null

  return (
    <div className="space-y-2 border-t pt-3 text-sm">
      {hasSubtotal && <MoneyRow label="Subtotal" amountCents={subtotalCents ?? 0} />}
      {hasDiscount && <MoneyRow label="Discount" amountCents={-(discountCents ?? 0)} icon={Percent} />}
      {hasTax && <MoneyRow label="Sales Tax" amountCents={taxCents ?? 0} />}
      {hasTip && tipIncludedInTotal === false && <MoneyRow label="Document Total" amountCents={totalCents} />}
      {hasTip && <MoneyRow label="Gratuity" amountCents={tipCents ?? 0} />}
      <MoneyRow label="Total" amountCents={displayTotalCents} strong />
    </div>
  )
}

function getTipAwareTotals({
  subtotalCents,
  discountCents,
  taxCents,
  tipCents,
  totalCents,
}: {
  subtotalCents?: number
  discountCents?: number
  taxCents?: number
  tipCents?: number
  totalCents: number
}): { displayTotalCents: number; tipIncludedInTotal: boolean } {
  const tip = tipCents ?? 0
  if (tip <= 0) return { displayTotalCents: totalCents, tipIncludedInTotal: true }

  if (subtotalCents !== undefined) {
    const calculatedTipExclusiveTotal = subtotalCents - (discountCents ?? 0) + (taxCents ?? 0)
    if (totalCents === calculatedTipExclusiveTotal) {
      return { displayTotalCents: totalCents + tip, tipIncludedInTotal: false }
    }
  }

  return { displayTotalCents: totalCents, tipIncludedInTotal: true }
}

function MoneyRow({
  label,
  amountCents,
  icon: Icon,
  strong,
}: {
  label: string
  amountCents: number
  icon?: LucideIcon
  strong?: boolean
}) {
  const isNegative = amountCents < 0

  return (
    <div className={cn('flex items-center justify-between gap-4', strong && 'border-t-2 border-foreground pt-2 font-semibold')}>
      <span className="flex items-center gap-1.5 text-muted-foreground">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {label}
      </span>
      <span className={cn('font-financial', isNegative && 'text-status-success-fg')}>
        {isNegative ? '-' : ''}${formatCurrency(Math.abs(amountCents))}
      </span>
    </div>
  )
}

type LineItem = QuoteLineItem | InvoiceLineItem

export function DocumentLineItemsCard({
  kind,
  items,
  subtotalCents,
  discountCents,
  taxCents,
  tipCents,
  totalCents,
  onEditClick,
  isEditable = false,
}: {
  kind: DocumentKind
  items: LineItem[]
  subtotalCents: number
  discountCents?: number
  taxCents?: number
  tipCents?: number
  totalCents: number
  onEditClick?: () => void
  isEditable?: boolean
}) {
  const hasTax = (taxCents ?? 0) > 0
  const discountAmountCents = discountCents ?? 0
  const discountRatio = subtotalCents > 0 ? (subtotalCents - discountAmountCents) / subtotalCents : 1
  const totals = getTipAwareTotals({
    subtotalCents,
    discountCents,
    taxCents,
    tipCents,
    totalCents,
  })

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base">
              Line Items
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({items.length} {items.length === 1 ? 'item' : 'items'})
              </span>
            </CardTitle>
          </div>
          {isEditable && onEditClick && (
            <Button type="button" variant="outline" size="sm" onClick={onEditClick}>
              Edit {kind === 'quote' ? 'Quote' : 'Invoice'}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            <Package className="mx-auto mb-2 h-10 w-10 opacity-50" />
            <p>No line items</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="border-b text-xs uppercase text-muted-foreground">
                    <th className="py-2 pr-4 text-left">Item</th>
                    <th className="w-20 px-2 py-2 text-right">Qty</th>
                    <th className="w-28 px-2 py-2 text-right">Unit Price</th>
                    {hasTax && <th className="w-24 px-2 py-2 text-right">Tax</th>}
                    <th className="w-28 py-2 pl-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.map((item, index) => (
                    <tr key={item.id || index}>
                      <td className="py-3 pr-4">
                        <p className="text-sm font-medium">{item.name || 'Unnamed item'}</p>
                        {item.description && (
                          <SafeHtml
                            html={item.description}
                            className="mt-0.5 line-clamp-2 text-xs text-muted-foreground"
                          />
                        )}
                      </td>
                      <td className="font-financial px-2 py-3 text-right text-sm">{item.quantity}</td>
                      <td className="font-financial px-2 py-3 text-right text-sm">${formatCurrency(Number(item.unitPriceCents || 0n))}</td>
                      {hasTax && (
                        <td className="font-financial px-2 py-3 text-right text-sm text-muted-foreground">
                          {Number(item.salesTaxCents || 0n) > 0 ? `$${formatCurrency(Number(item.salesTaxCents || 0n))}` : '-'}
                        </td>
                      )}
                      <td className="font-financial py-3 pl-2 text-right text-sm font-medium">
                        {discountRatio < 1 ? (
                          <div className="space-y-0.5">
                            <div className="text-xs font-normal text-muted-foreground line-through">
                              ${formatCurrency(Number(item.lineTotalCents || 0n))}
                            </div>
                            <div>
                              ${formatCurrency(Math.round(Number(item.lineTotalCents || 0n) * discountRatio))}
                            </div>
                          </div>
                        ) : (
                          <>${formatCurrency(Number(item.lineTotalCents || 0n))}</>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="ml-auto max-w-sm">
              <DocumentTotalsRows
                subtotalCents={subtotalCents}
                discountCents={discountCents}
                taxCents={taxCents}
                tipCents={tipCents}
                totalCents={totalCents}
                tipIncludedInTotal={totals.tipIncludedInTotal}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

type Schedule = QuotePaymentSchedule | PaymentSchedule

function isQuoteSchedule(schedule: Schedule): schedule is QuotePaymentSchedule {
  return 'quoteId' in schedule
}

function getScheduleAmount(schedule: Schedule): number {
  if (isQuoteSchedule(schedule)) {
    return Number(schedule.amountWithTipCents ?? schedule.calculatedAmountCents ?? schedule.amountCents ?? 0n)
  }
  return Number(schedule.amountCents || 0n)
}

function getScheduleDescription(schedule: Schedule, index: number): string {
  return schedule.description || `Payment ${index + 1}`
}

function getQuoteScheduleAmountLabel(schedule: QuotePaymentSchedule): string | null {
  if (schedule.amountType === QuotePaymentScheduleAmountTypeEnum.QUOTE_PAYMENT_SCHEDULE_AMOUNT_TYPE_PERCENTAGE && schedule.percentage !== undefined) {
    return `${schedule.percentage}%`
  }
  return null
}

export function DocumentPaymentScheduleCard({
  schedules,
}: {
  schedules: Schedule[]
}) {
  const sortedSchedules = React.useMemo(
    () => [...schedules].sort((a, b) => a.sequenceNumber - b.sequenceNumber),
    [schedules],
  )

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-base">
            Payment Schedule
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              ({sortedSchedules.length} {sortedSchedules.length === 1 ? 'payment' : 'payments'})
            </span>
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {sortedSchedules.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            <Calendar className="mx-auto mb-2 h-10 w-10 opacity-50" />
            <p>No payment schedule defined</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sortedSchedules.map((schedule, index) => {
              const amountLabel = isQuoteSchedule(schedule) ? getQuoteScheduleAmountLabel(schedule) : null
              return (
                <div key={schedule.id || index} className="rounded-lg border bg-muted/30 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-muted-foreground">Payment {index + 1}</p>
                      <p className="mt-1 text-sm font-medium">{getScheduleDescription(schedule, index)}</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {isQuoteSchedule(schedule)
                          ? formatQuotePaymentScheduleDueFromProto(schedule)
                          : schedule.dueDate
                            ? `Due ${formatTimestamp(timestampToDateOnly(schedule.dueDate))}`
                            : 'Due date not set'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-financial text-lg font-semibold">${formatCurrency(getScheduleAmount(schedule))}</p>
                      {amountLabel && <p className="text-xs text-muted-foreground">{amountLabel}</p>}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export function LinkedRecordCard({
  title,
  rows,
}: {
  title: string
  rows: Array<{
    label: string
    value: React.ReactNode
    action?: React.ReactNode
  }>
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {rows.map((row) => (
          <div key={row.label} className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">{row.label}</p>
              <div className="mt-0.5 truncate text-sm font-medium">{row.value}</div>
            </div>
            {row.action}
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

export function DocumentStatCard({
  label,
  amountCents,
  count,
  noun = 'document',
  isActive,
  onClick,
}: {
  label: string
  amountCents: number
  count: number
  noun?: string
  isActive?: boolean
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-lg border bg-card p-4 text-left transition-colors hover:bg-accent/50',
        isActive && 'border-primary bg-primary/5 ring-1 ring-primary',
      )}
    >
      <p className="font-financial text-2xl font-bold">${formatCurrency(amountCents)}</p>
      <p className="mt-2 font-medium">{label}</p>
      <p className="mt-1 text-sm text-muted-foreground">
        {count} {count === 1 ? noun : `${noun}s`}
      </p>
    </button>
  )
}

export function EmptyDocumentDescription({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed py-12 text-center text-muted-foreground">
      <Receipt className="mx-auto mb-2 h-10 w-10 opacity-50" />
      <p>{children}</p>
    </div>
  )
}
